import ApiService from '@/data/services/ApiService'
import { AxiosResponse } from 'axios'
import { IServiceResponse } from '@/data/models'

export interface INutritionProfile {
  dietary_profile?: string
  allergies?: string
  supplements?: string
  is_dietary_profile_player_visible?: boolean
  is_supplements_player_visible?: boolean
}

export interface IFoodItem {
  id: number
  external_id: string
  name: string
  brand?: string
  energy_kcal?: number
  protein?: number
  carbohydrates?: number
  fat?: number
  fiber?: number
  sugar?: number
  serving_size?: number
  serving_unit?: string
}

export interface IFoodIntakeCreate {
  player_id: number
  food_external_id: string
  meal_type_id: number
  amount: number
  unit: string
  consumed_at_date: string
  consumed_at?: string
}

export interface IFoodIntakeUpdate {
  player_id: number
  amount?: number
  meal_type_id?: number
  consumed_at?: string
}

export interface IFoodIntake {
  id?: number
  player_id: number
  food_external_id: string
  meal_type_id: number
  amount: number
  unit: string
  consumed_at_date: string
  consumed_at?: string
  food?: IFoodItem
}

export interface IMealType {
  id: number
  name: string
  order?: number
}

export class NutritionApiService extends ApiService {
  static async updateNutritionProfile(playerId: number, data: INutritionProfile): Promise<IServiceResponse<object>> {
    return this.axios.put(`players/${playerId}/nutrition-profile`, data)
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getMealTypes(): Promise<IServiceResponse<IMealType[]>> {
    return this.axios.get('nutrition-meal-types')
      .then((response: AxiosResponse<IServiceResponse<IMealType[]>>) => response.data)
  }

  static async searchFood(query: string, limit = 20, page = 1, lang = 'english'): Promise<IServiceResponse<IFoodItem[]>> {
    return this.axios.get('nutrition/food', { params: { query, limit, page, lang } })
      .then((response: AxiosResponse<IServiceResponse<IFoodItem[]>>) => response.data)
  }

  static async getFoodById(foodId: number, amount?: number): Promise<IServiceResponse<IFoodItem>> {
    const params: Record<string, any> = {}
    if (amount !== undefined) params.amount = amount
    return this.axios.get(`nutrition/food/${foodId}`, { params })
      .then((response: AxiosResponse<IServiceResponse<IFoodItem>>) => response.data)
  }

  static async getFoodByBarcode(barcode: string): Promise<IServiceResponse<IFoodItem>> {
    return this.axios.get(`nutrition/food/barcode/${barcode}`)
      .then((response: AxiosResponse<IServiceResponse<IFoodItem>>) => response.data)
  }

  static async createFoodIntake(data: IFoodIntakeCreate): Promise<IServiceResponse<IFoodIntake>> {
    return this.axios.post('nutrition/food-intake', data)
      .then((response: AxiosResponse<IServiceResponse<IFoodIntake>>) => response.data)
  }

  static async updateFoodIntake(intakeId: string, data: IFoodIntakeUpdate): Promise<IServiceResponse<IFoodIntake>> {
    return this.axios.put(`nutrition/food-intake/${intakeId}`, data)
      .then((response: AxiosResponse<IServiceResponse<IFoodIntake>>) => response.data)
  }

  static async deleteFoodIntake(intakeId: string, playerId: number): Promise<AxiosResponse> {
    return this.axios.delete(`nutrition/food-intake/${intakeId}`, { data: { player_id: playerId } })
      .then(response => response.data)
  }

  static async getNutritionSources(): Promise<IServiceResponse<object[]>> {
    return this.axios.get('nutrition-sources')
      .then((response: AxiosResponse<IServiceResponse<object[]>>) => response.data)
  }

  static async getProgressMeasurements(playerId: number, params?: object): Promise<object> {
    return this.axios.get(`players/${playerId}/progress-measurements`, { params })
      .then((response: AxiosResponse) => response.data)
  }

  static async uploadProgressPhoto(playerId: number, formData: FormData): Promise<object> {
    return this.axios.post(`players/${playerId}/progress-measurements`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then((response: AxiosResponse) => response.data)
  }

  static async deleteProgressMeasurement(playerId: number, measurementId: number): Promise<AxiosResponse> {
    return this.axios.delete(`players/${playerId}/progress-measurements/${measurementId}`)
  }

  static async deleteProgressPhoto(playerId: number, measurementId: number, mediaId: number): Promise<object> {
    return this.axios.delete(`players/${playerId}/progress-measurements/${measurementId}/media/${mediaId}`)
      .then((response: AxiosResponse) => response.data)
  }

  static async calculateFoodNutrients(foodExternalId: string, amount: number): Promise<IServiceResponse<object>> {
    return this.axios.get('nutrition/food/calculation', {
      params: { food_external_id: foodExternalId, amount }
    }).then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }
}
